// AuthContext.js
import { createContext, useState, useContext, useEffect } from "react";
import { UserApi } from "../api/AuthApi";
import { EmailApi } from "../api/EmailApi";
import { logger } from "../utils/logger";
import { getErrorMessage } from "../utils/errorMessages";
import { toast } from "sonner";
import { setAccessToken } from "../api/axiosInstance";
import { RefreshTokenApi } from "../api/RefreshTokenApi";

const AuthContext = createContext();

const updateToken = (newToken) => {
  setAccessToken(newToken);
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const rs = await RefreshTokenApi.refresh();
        setAccessToken(rs.data.accessToken);
        setIsAuthenticated(true);
      } catch {
        // No valid cookie — stay unauthenticated
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, []);

  const signIn = async (authRequest) => {
    try {
      const response = await UserApi.login(authRequest);
      const responseData = response.data;
      updateToken(responseData.accessToken);
      setIsAuthenticated(true);
      setUser({
        username: authRequest.username,
        email: responseData.userDisplayDetails.email,
      });
      return null;
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Login failed", error, {
        correlationId,
        username: authRequest.username,
      });

      toast.error(errorMessage);
      return handleErrors(error);
    }
  };

  const signUp = async (signUpRequest) => {
    try {
      await UserApi.signUp(signUpRequest);
      return { success: true, email: signUpRequest.email };
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Sign up failed", error, {
        correlationId,
        username: signUpRequest.username,
      });

      toast.error(errorMessage);
      return handleErrors(error);
    }
  };

  const verifyEmail = async (verifyRequest) => {
    try {
      const response = await EmailApi.verifyEmail(verifyRequest);
      const responseData = response.data;
      updateToken(responseData.accessToken);
      setIsAuthenticated(true);
      setUser({
        username: responseData.userDisplayDetails.username,
        email: responseData.userDisplayDetails.email,
      });
      return { success: true };
    } catch (error) {
      return handleErrors(error);
    }
  };

  const resendVerificationEmail = async (email) => {
    try {
      await EmailApi.resendVerificationEmail({ email });
      return { success: true };
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Resend verification failed", error, { correlationId });

      toast.error(errorMessage);
      return handleErrors(error);
    }
  };

  function handleErrors(exception) {
    let errors = {};
    let errorResponse = exception?.response?.data;
    let errorStatus = errorResponse?.status;

    if (!errorResponse || !errorStatus) {
      errors.code = 500;
      errors.general = "An unexpected error occurred. Please try again.";
      return errors;
    }

    errors.code = errorStatus;

    if (errorStatus === 409) {
      const errorMessage = errorResponse.message;
      errors.username = errorMessage;
    } else {
      errors.general = errorResponse.message;
    }

    return errors;
  }

  const logout = async () => {
    await UserApi.logout();
    setIsAuthenticated(false);
    updateToken(null);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        signIn,
        logout,
        signUp,
        verifyEmail,
        resendVerificationEmail,
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
